import React from 'react';
import { Link } from 'react-router-dom';
import { FaBoxes, FaIndustry, FaVials, FaTruck, FaCheckDouble } from 'react-icons/fa';
import PageHero from '../components/PageHero';

const steps = [
  {
    icon: <FaBoxes size={22}/>, num:'01',
    title: 'Integrated Component Sourcing',
    desc: 'Complete supply chain management — global and local vendor networks for both standard and specialised components. We eliminate supply chain fragmentation and ensure quality at source.',
    tags: ['Global Procurement','Approved Vendor List','BOM Management','Kitting'],
  },
  {
    icon: <FaIndustry size={22}/>, num:'02',
    title: 'Agile Manufacturing Platform',
    desc: 'Flexible production from 10 prototype units up to 100K+ in series. Three SMT lines with PanaCIM full traceability software, 125K CPH throughput, and 4-sigma placement accuracy.',
    tags: ['SMT Assembly','THT Assembly','BGA Rework','Conformal Coating'],
  },
  {
    icon: <FaVials size={22}/>, num:'03',
    title: 'Quality Control & Testing',
    desc: 'Inline SPI, AOI, X-ray inspection, ICT, and functional testing at every step. Certified IPC Class II and III. PanaCIM real-time traceability on every board produced.',
    tags: ['Inline SPI / AOI','X-Ray Inspection','ICT & FCT','UL COC & Traceability'],
  },
  {
    icon: <FaTruck size={22}/>, num:'04',
    title: 'Logistics & After-Sales Support',
    desc: 'Warehousing, dispatch management, and integrated logistics ensure on-time delivery. We also provide repair services and full product life cycle management post-delivery.',
    tags: ['Warehousing','OTD Tracking','Repair Services','Field Returns Handling'],
  },
];

const perf = [
  { value:'20M+',   label:'Smart Meters Produced',    sub:'IoT / Bluetooth segment' },
  { value:'96–99%', label:'On-Time Delivery',          sub:'Across all programmes' },
  { value:'<750',   label:'PPM Defect Rate',           sub:'Monthly average' },
  { value:'125K',    label:'CPH SMT Throughput',        sub:'4-sigma accuracy' },
];

const specials = [
  'Gluing & Conformal Coating (spray & manual)',
  'UL-required assembly process with COC',
  'In-house jig & fixture development',
  'High-mix multi-product assembly (12+ variants)',
  'Smart Meter full product engagement (IoT/BT)',
  'Specialised harness for Railway & Industrial',
];

const getImageForStep = (num) => {
  switch(num) {
    case '01': return 'https://images.pexels.com/photos/6755083/pexels-photo-6755083.jpeg';
    case '02': return 'https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg';
    case '03': return 'https://images.pexels.com/photos/36522029/pexels-photo-36522029.jpeg';
    case '04': return 'https://images.pexels.com/photos/23931580/pexels-photo-23931580.jpeg';
    default: return 'https://images.unsplash.com/photo-1581092160607-1d4c1c7a2c2d';
  }
};

const CurveDivider = () => (
  <div style={{ overflow: 'hidden', lineHeight: 0, marginTop: '-20px' }}>
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 1200 120" 
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100px', fill: '#dcfce7', display: 'block' }}
    >
      <path d="M0,0V60C150,125 300,10 450,65C600,125 750,15 900,70C1050,125 1200,40 1200,40V0H0Z" />
    </svg>
  </div>
);

export default function EMSSolutions() {
  return (
    <div>
      <PageHero
        chip="EMS Solutions"
        title="Turnkey Electronic Manufacturing Services"
        subtitle="One partner from component sourcing to final delivery. Scalable EMS built for safety-critical industries."
      />

      {/* PROCESS SECTION */}
      <section style={{ padding: '100px 0', background: 'linear-gradient(135deg, #f8fafc 0%, #ecfdf5 100%)' }}>
        <div className="wrap" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <span style={{ color: '#166534', fontWeight: 700, letterSpacing: '2px', fontSize: '13px' }}>OUR PROCESS</span>
            <h2 style={{ fontSize: '42px', fontWeight: 800, fontFamily: 'Rajdhani', color: '#0f172a', marginTop: '12px' }}>
              End-to-End EMS Excellence
            </h2>
          </div>

          {steps.map((s, index) => (
            <React.Fragment key={s.num}>
              <div 
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '80px',
                  alignItems: 'center',
                  marginBottom: index !== steps.length - 1 ? '60px' : '0',
                }}
                className="step-grid reveal"
              >
                {/* Image */}
                <div style={{ 
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.12)',
                  order: index % 2 === 0 ? 1 : 2
                }}>
                  <img 
                    src={getImageForStep(s.num)} 
                    alt={s.title}
                    style={{ 
                      width: '100%', 
                      height: '460px', 
                      objectFit: 'cover', 
                      display: 'block',
                      transition: 'transform 0.6s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  />
                </div>

                {/* Content */}
                <div style={{ order: index % 2 === 0 ? 2 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                    <div style={{
                      fontFamily: 'Rajdhani',
                      fontSize: '78px',
                      fontWeight: 800,
                      color: '#166534',
                      lineHeight: 1,
                      opacity: 0.18,
                    }}>
                      {s.num}
                    </div>
                    <div style={{ color: '#166534' }}>{s.icon}</div>
                  </div>

                  <h3 style={{ fontSize: '29px', fontWeight: 700, marginBottom: '20px', color: '#0f172a', fontFamily: 'Rajdhani' }}>
                    {s.title}
                  </h3>

                  <p style={{ fontSize: '16.5px', lineHeight: '1.75', color: '#475569', marginBottom: '32px' }}>
                    {s.desc}
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {s.tags.map(tag => (
                      <span key={tag} style={{
                        background: '#f0fdf4',
                        color: '#166534',
                        padding: '8px 18px',
                        borderRadius: '30px',
                        fontSize: '13.5px',
                        fontWeight: 500
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {index !== steps.length - 1 && <CurveDivider />}
            </React.Fragment>
          ))}
        </div>

        {/* RESPONSIVE CSS */}
        <style>{`
          @media (max-width: 768px) {
            .step-grid {
              grid-template-columns: 1fr !important;
              gap: 40px !important;
            }
            .step-grid > div {
              order: 0 !important;
            }
          }
        `}</style>
      </section>

      {/* Performance Section */}
      <section style={{ background: '#166534', padding: '52px 0' }}>
        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 24px' }}>
          <div className="g4" style={{ textAlign:'center' }}>
            {perf.map(p => (
              <div key={p.label}>
                <div style={{ fontFamily:'Rajdhani', fontSize:44, fontWeight:700, color:'#fff', lineHeight:1 }}>{p.value}</div>
                <div style={{ fontSize:14, color:'rgba(255,255,255,0.9)', marginTop:6, fontWeight:600 }}>{p.label}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:3 }}>{p.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Capabilities */}
      <section className="sec">
        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:56, alignItems:'center' }} className="spec-grid">
            <div className="reveal">
              <span className="chip">Special Capabilities</span>
              <h2 className="h2">Beyond Standard EMS</h2>
              <p style={{ color:'#64748b', lineHeight:1.78, marginBottom:26 }}>
                We go beyond conventional assembly — handling complex, high-mix, multi-product programmes with demanding process and documentation requirements.
              </p>
              <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:10 }}>
                {specials.map(s => (
                  <li key={s} style={{ display:'flex', gap:10, fontSize:14, color:'#334155' }}>
                    <FaCheckDouble style={{ color:'#166534', flexShrink:0, marginTop:2 }} size={13}/>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal">
              <div className="card" style={{ background:'#1a1a2e', border:'none', padding:'36px 30px' }}>
                <p style={{ fontSize:11, fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#4ade80', marginBottom:16 }}>
                  Volume Flexibility
                </p>
                {[['10','Prototype Min'],['10,000','Mid Volume'],['100,000+','Mass Production']].map(([v,l]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 0', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize:13, color:'#94a3b8' }}>{l}</span>
                    <span style={{ fontFamily:'Rajdhani', fontSize:26, fontWeight:700, color:'#4ade80' }}>{v}</span>
                  </div>
                ))}
                <p style={{ fontSize:13, color:'#64748b', marginTop:18 }}>
                  All volume tiers receive the same IPC-certified quality processes and PanaCIM traceability.
                </p>
              </div>
            </div>
          </div>
        </div>
        <style>{`@media(max-width:768px){.spec-grid{grid-template-columns:1fr!important}}`}</style>
      </section>

      {/* CTA */}
      <section className="sec-sm bg-green" style={{ textAlign:'center' }}>
        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 24px' }}>
          <h2 className="h2" style={{ marginBottom:10 }}>Ready for a turnkey EMS partner?</h2>
          <p className="sub" style={{ margin:'0 auto 26px' }}>Share your requirements and we'll create a customised programme.</p>
          <Link to="/contact" className="btn btn-primary">Request a Quote</Link>
        </div>
      </section>
    </div>
  );
}